import type { User } from '../event/types';

import { AGORA, LOCAL_STORAGE_KEYS } from '@/constants';
import { getValueFromLocalstore } from '@/services/localStorage.service';

export const LiteModeKeys: {
  liteModeLocalStoreKey: string;
  supressedLiteModeStoreKey: string;
} = {
  liteModeLocalStoreKey: 'liteModeActive',
  supressedLiteModeStoreKey: 'supressLiteModeConfirmation'
};

export type VideoQuality = {
  label: 'Auto' | '180p' | '240p' | '360p' | '480p' | '720p' | '1080p';
  name: string;
  value: '480p_8' | '180p_1' | '240p_4' | '360p_9' | '720p_1' | '1080p_1';
};

export type UserState = {
  currentUser?: CurrentUser;
  currentEventUser?: User;
  accessToken: string;
  refreshToken?: string;
  privateDiscussionRoom: {
    id: string | null;
  } | null;
  liteMode: LiteMode;
  selected_video_quality?: VideoQuality;
  hlsPlaybackSupported: boolean;
  techCheckedEvents: Array<string>;
  isWatchingLive: Record<string, boolean>; // Broadcasts that are being watched live
  cognitoUserDetails?: any;
  permissions?: {
    broadcasts: { [key: string]: string[] };
    events: { [key: string]: string[] };
  };
};

export type CurrentUser = User & {
  sessionID?: string;
  signature?: string;
};

export type LiteMode = {
  liteModeActive: boolean;
  supressedLiteModeConfirmation: boolean;
  liteModeState:
    | 'LOADED'
    | 'LOADINGTOLITEMODE'
    | 'LOADINGTOLIVEMODE'
    | 'LITEMODESWITCHSUCCESS'
    | 'LIVEMODESWITCHSUCCESS'
    | 'CONFRIMLITEMODESWITCH'
    | 'PROMPTLITEMODESWITCH'
    | 'PROMPTLIVEMODESWITCH'
    | 'INFORMWHENAVAILABLEPROMPT'
    | 'AVAILABLEAGAINPROMPT';
  lastLiteModeSwitchPromptTimestamp?: number;
  lastLiveModeSwitchPromptTimestamp?: number;
  willShowLiveModeAvailablePrompt?: boolean;
  liteModeAvailablityPromptTimeout?: null | ReturnType<typeof setTimeout>;
};

export const getDefaultState = (): UserState => {
  return {
    // reaperInterval: null,
    currentUser: undefined,
    currentEventUser: undefined,
    accessToken: '',
    refreshToken: '',
    privateDiscussionRoom: null,
    liteMode: {
      liteModeActive: getValueFromLocalstore(LiteModeKeys.liteModeLocalStoreKey, false),
      supressedLiteModeConfirmation: getValueFromLocalstore(LiteModeKeys.supressedLiteModeStoreKey, false),
      liteModeState: 'LOADED'
    },
    selected_video_quality: getValueFromLocalstore(LOCAL_STORAGE_KEYS.selectedVideoQuality, AGORA.VIDEO_QUALITY[0]),
    hlsPlaybackSupported: false,
    techCheckedEvents: getValueFromLocalstore(LOCAL_STORAGE_KEYS.techCheckedEvents, []),
    isWatchingLive: {},
    cognitoUserDetails: undefined
  };
};

export enum BroadcastActions {
  GO_BACKSTAGE = 'go_backstage',
  LAUNCH_ALERT_MESSAGE_TO_STAGE = 'launch_alert_message_to_stage',
  JOIN_LEAVE_STAGE = 'join_leave_stage',
  SHARE_SCREEN = 'share_screen',
  START_STOP_MANUAL_MODE_SESSIONS = 'start_stop_manual_mode_sessions',
  START_STOP_VIDEO = 'start_stop_video',
  CONTROL_SLIDES = 'control_slides',
  MODIFY_LAYOUT = 'modify_layout',
  MODIFY_OVERLAY = 'modify_overlay',
  CONTROL_SESSION_TICKER = 'control_session_ticker',
  MODIFY_LOGOS = 'modify_logos',
  CONTROL_Q_AND_A = 'control_q_and_a',
  CONTROL_POLLS = 'control_polls',
  CONTROL_CHAT = 'control_chat',
  LAUNCH_SURVEY = 'launch_survey',
  MOVE_SPEAKERS_STAGE = 'move_speakers_stage',
  MUTE_SPEAKERS = 'mute_speakers',
  MUTE_STAFF = 'mute_staff',
  VIEW_ATTENDEE_COUNT = 'view_attendee_count',
  MODIFY_RUN_OF_SHOW = 'modify_run_of_show_config',
  MODIFY_LIVE_RUN_OF_SHOW = 'modify_live_run_of_show',
  ANSWER_Q_AND_A = 'answer_q_and_a',
  SHARE_Q_AND_A = 'share_q_and_a',
  SHARE_POLLS = 'share_polls',
  SHARE_SESSION_CHAT = 'share_session_chat',
  PUSH_SESSION_CHAT_TO_Q_AND_A = 'push_session_chat_to_q_and_a',
  VIEW_POLLS = 'view_polls',
  VIEW_RAISE_HAND_REQUESTS = 'view_raise_hand_requests',
  VIEW_Q_AND_A = 'view_q_and_a',
  CONTROL_PLAY_AND_PAUSE = 'control_play_and_pause',
  CONTROL_STAGE_DIRECTION = 'control_stage_direction'
}

export enum EventActions {
  ViewAttendeeView = 'view_attendee_view',
  ControlEventTicker = 'control_event_ticker',
  StartInPersonStreaming = 'start_in_person_streaming',
  ModeratePublicChat = 'moderate_public_chat',
  StartStopManualAgendaItem = 'start_stop_manual_mode_agenda_item',
  ControlBoothAndRoomChat = 'control_booth_and_room_chat',
  MUTE_ROOM_ATTENDEES = 'mute_room_attendees',
  END_EVENT = 'end_event'
}
